import React from 'react';
import PropTypes from 'prop-types';

import { useMutation } from '@sopranodesign/pulse-hooks';
import {
  Flex,
  Button,
  Divider,
  useNotification
} from '@sopranodesign/pulse-components';
import {
  useConditionalReducer,
  Conditionals,
  AddConditionalButton,
  ConditionalContext,
  Match
} from '@sopranodesign/pulse-conditionals';

import Modal from '../../../Modal';
import { conditionals } from '../../../../../../subjects/index';
import { useBuilder } from '../../../../hooks/index';
import { createStepTransition } from '../../../../api/index';
import TransitionPreview from './TransitionPreview';
import { timeConditionals, formatTimeValue } from '../Trigger/AddTriggerNode';

const AddTransitionNode = ({ handleClose, link }) => {
  const { dispatch } = useBuilder();

  const conditional = useConditionalReducer({
    subjects: conditionals
  });

  const { showSuccess } = useNotification();

  const { isPending, mutate } = useMutation({
    mutationFn: createStepTransition,
    onSuccess: ({ data }) => {
      showSuccess('Successfully added transition');
      dispatch({
        type: 'ADD_TRANSITION',
        stepId: link.from.id,
        transitionDetails: data.data
      });
      handleClose();
    }
  });

  const handleSubmit = event => {
    event.preventDefault();

    if (conditional.valid && !isPending) {
      if (
        timeConditionals.includes(conditional.form.conditionals[0].type) &&
        conditional.form.conditionals.length === 1
      ) {
        const formattedTime = formatTimeValue(conditional.form);

        mutate({
          stepId: link.from.id,
          transition: {
            description: `Transition to step #${link.to.id}`,
            previous_transition: null,
            next_transition: {
              id: link.to.id,
              ...formattedTime,
              x: link.to.base.x,
              y: link.to.base.y - 100,
              variables: [],
              metrics: []
            }
          }
        });
      } else {
        mutate({
          stepId: link.from.id,
          transition: {
            description: `Transition to step #${link.to.id}`,
            previous_transition: null,
            next_transition: {
              id: link.to.id,
              ...conditional.form,
              x: link.to.base.x,
              y: link.to.base.y - 100,
              variables: [],
              metrics: []
            }
          }
        });
      }
    }
  };

  const startNode = {
    id: link.from.id,
    blocks: [],
    base: link.from.base
  };

  const destinationNode = {
    id: link.to.id,
    blocks: [],
    base: link.to.base
  };

  return (
    <Modal
      header="Add transition"
      handleClose={handleClose}
      style={{ maxWidth: '100%', width: '80rem' }}>
      <Flex>
        <Flex borderRight col fat="true" pad="true">
          <Flex as="form" col tall onSubmit={handleSubmit}>
            <ConditionalContext.Provider value={conditional}>
              <Match />
              <Divider />
              <Conditionals labelPrefix="transition-conditionals" />
              <Flex right>
                <AddConditionalButton />
              </Flex>
              <Divider />
              <Flex center xSpace bottom>
                <Flex bottom fat="true" right xSpace>
                  <Button
                    variant="primary"
                    disabled={!conditional.valid || isPending}
                    type="submit">
                    Save
                  </Button>
                </Flex>
              </Flex>
            </ConditionalContext.Provider>
          </Flex>
        </Flex>
        <TransitionPreview
          startNode={startNode}
          destinationNode={destinationNode}
        />
      </Flex>
    </Modal>
  );
};

AddTransitionNode.propTypes = {
  handleClose: PropTypes.func.isRequired,
  link: PropTypes.shape({
    from: PropTypes.shape({
      id: PropTypes.number.isRequired,
      base: PropTypes.object.isRequired
    }).isRequired,
    to: PropTypes.shape({
      id: PropTypes.number.isRequired,
      base: PropTypes.object.isRequired
    }).isRequired
  })
};

export default AddTransitionNode;
